import { Link, withPrefix } from "gatsby"
import React from "react"

import pollution from "../../images/pollution.jpg"
import freeway from "../../images/living-near-freeway.jpg"
import soot from "../../images/soot.jpg"
import cleanAir from "../../images/clean-air.png"


import "./proposal.css"

const ProposalComponent = () => (
  <section className="section proposal">
    <h1 className="sectionTitle">Freeway Air Toxins</h1>
      <div className="titleImageWrapper imageWrapper">
        <img className="titleImage" src={pollution} alt="pollution in LA"/>
        <p className="photoCaption rightAligned"><a href="https://well.blogs.nytimes.com/2015/03/04/childrens-lung-health-improves-as-air-pollution-is-reduced-study-says/">Photo source.</a></p>
      </div>
      <div className="pageNav">
        <ul>
          <Link to="/proposal/#background"><li>Background</li></Link>
          <Link to="/proposal/#problem"><li>The Problem</li></Link>
          <Link to="/proposal/#solution"><li>Our Solution to the Problem</li></Link>
          <Link to="/proposal/#faq"><li>FAQ</li></Link>
          <Link to="/proposal/#fact-sheet"><li>Fact Sheet and References</li></Link>
        </ul>
      </div>
      <div className="subsectionWrapper grey">
        <div className="subsection background" id="background">
          <h2 className="subsectionTitle">Background</h2>
          <div className="pdfLink">
            <a href={withPrefix('/proposal-background.pdf')}>View Full Background Sheet</a>
          </div>
          <div className="imageBlock">
            <div className="imageWrapper">
              <img src={freeway} alt="living near a freeway"/>
            </div>
            <div className="imageCaption">
              <p>Living near a Los Angeles County freeway</p>
              <p>Photo courtesy of <i>Los Angeles Times</i></p>
            </div>
          </div>
          <div className="textBlock">
            <p>
              For at least 15 years, California air-quality officials have warned against building homes
              within <b>500 feet of freeways</b>. They know that repeated scientific findings show that living
              within 1000 feet of freeways <b>increases rates of Alzheimer’s, asthma, autism,
              dementia, heart attacks, lung cancer, childhood obesity, strokes, lung cancer,
              and pre-term births</b>.
            </p>
            <p>
              However, politicians often approve near-freeway housing because California needs
              affordable housing and they want additional tax revenues for their cities. Developers
              also are eager to build near-freeway housing, even in pollution hot spots, because they
              often get financial benefits from the state of California. To reduce greenhouse gases
              and increase mass transit, the state rewards developers who build near mass transit,
              and mass transit is often near freeways.
            </p>
            <p>
              Politicians and developers also typically claim that using air filters in near-freeway
              homes will make them safe. However, California health officials say this claim is false
              because such filters don’t capture most of the diesel particles that cause nearly all
              freeway-related cancers.
            </p>
            <p>
              Instead, California health officials want areas near freeways to be buffer zones, filled
              with vegetation, or used for commercial purposes, like warehouses&mdash;and not used for
              schools, retail, or residential spaces. They are especially concerned about risks to
              children from living or going to school near freeways. After all, US EPA confirms that
              <b> when adults and children receive the same pollution exposure, children are hurt
              roughly 10 times more than adults</b>.
            </p>
          </div>
        </div>
      </div>
      <div className="subsectionWrapper">
        <div className="subsection problem" id="problem">
          <h2 className="subsectionTitle">The Problem</h2>
          <div className="pdfLink">
            <a href={withPrefix('/proposal-problem.pdf')}>View Full Problem Sheet</a>
          </div>
          <div className="imageBlock">
            <div className="imageWrapper">
              <img src={soot} alt="soot after wiping a finger on a windowsill near a freeway"/>
            </div>
            <div className="imageCaption">
              <p>Wiping a finger on a Los Angeles County near-freeway windowsill</p>
              <p>Photo courtesy of <i>Los Angeles Times</i></p>
            </div>
          </div>
          <div className="textBlock">
            <p>
              The city of Pasadena has recently done at least two things that severely threaten
              residents’ health.
            </p>
            <p>
              <b>First</b>, in the last several years, the city has approved construction of roughly 1230
              residences, mostly apartments, in the high-diesel-pollution zones beside, or within 700
              feet of, Interstate-210. This is directly <b>contrary to California Air Board
              recommendations</b> against life-threatening, near-freeway, residential development.
            </p>
            <p>
              <b>Second</b>, on 1-24-19, the city released its “East Pasadena Draft Vision.” (East Pasadena
              includes mainly the areas East of Hill Street and North of Colorado Boulevard.) The city’s
              “draft vision” called for massive rezoning of East Pasadena residential neighborhoods,
              so as to make them a “<b>commercial destination center</b> in the city.”
            </p>
            <p>
              Yet because of the I-210 freeway and natural wind patterns, state pollution data show
              that East Pasadena already has more diesel pollution than 70% of all California
              communities. Because <b>diesel particles are responsible for nearly all cancers</b> caused
              by Los Angeles County air pollution, and because East Pasadena is one of the worst
              diesel-pollution areas of the state, East Pasadena residents already suffer
              disproportionate health threats, compared to the rest of the city and the state.
            </p>
            <p>
              The city’s “vision” thus appears to condemn the 22,000 residents of East Pasadena to
              even worse health harm and more disproportionate-pollution deaths than they already
              face. <b>East Pasadena residents already have more freeway-caused autism, cancer,
              dementia, heart attacks, and strokes than 70% of other Californians</b>. Yet the city of
              Pasadena wants to <b>add to the health threats, deadly pollution, and traffic burdens
              of these 22,000 East Pasadena residents</b>. This is unfair, contradicts state health
              recommendations, and amounts to environmental injustice.
            </p>
          </div>
        </div>
      </div>
      <div className="subsectionWrapper grey">
        <div className="subsection solution" id="soluion">
          <h2 className="subsectionTitle">Our Solution to the Problem</h2>
          <div className="imageBlock">
            <div className="imageWrapper">
              <img src={cleanAir} alt="picketing for clean air"/>
            </div>
          </div>
          <div className="textBlock">
            <p>
              <b>First</b>, the city of Pasadena should <b>follow state health recommendations</b>. These
              recommendations warn against putting homes within 500-1000 feet of freeways. They
              warn that even air filters cannot adequately protect the health of people in near-freeway
              homes. They also urge near-freeway areas to be used either for vegetation buffers or for
              commercial enterprises like warehouses that avoid pollution exposure to humans.
            </p>
            <p>
              <b>Second</b>, the city of Pasadena should follow the example of Los Angeles. LA says a
              <b> 1,000 feet freeway-development boundary</b> is necessary to avoid the most “negative
              effects of air pollution caused by freeway proximity.”
            </p>
            <p>
              <b>Third</b>, the city of Pasadena should <b>prohibit any further</b> school, housing, or retail
              <b> development</b> in any residential areas of Pasadena that already are in the 40-percent-
              dirtiest-census-tracts for diesel pollution in the state. Additional commercial and retail
              development, <b>in already heavily polluted areas</b>, would only increase freeway, and
              especially diesel, pollution in these already-overburdened areas.
            </p>
            <p>
              <b>Fourth</b>, the city of Pasadena should <b>promote affordable housing</b> and retail
              development in bus and metro transit corridors that are not already heavily polluted by
              freeway diesel particulates.
            </p>
          </div>
        </div>
      </div>
      <div className="subsectionWrapper">
        <div className="subsection faq" id="faq">
          <h2 className="subsectionTitle">Frequently Asked Questions</h2>
          <div className="pdfLink">
            <a href={withPrefix('/proposal-faq.pdf')}>View Full FAQ Document</a>
          </div>
          <div className="textBlock faq">
            <ol>
              <li>
                <h3 className="faqQuestion">WHY ARE CITIZENS CONCERNED ABOUT FREEWAY AIR TOXINS?</h3>
                <p className="faqAnswer">
                  In recent years, the city of Pasadena has done two things that are extremely dangerous
                  to residents’ health. <b>First</b>, ignoring California Air Board recommendations not to site
                  schools or homes within 500 feet of freeways, Pasadena has approved thousands of
                  beside-freeway residences, including those for hundreds of children. As a result,
                  freeway-diesel-particle exposure will cause residents, living up to 1000 feet away, to
                  have increased cancer risks that are up to hundreds of times greater than they
                  otherwise would have.
                </p>
                <p className="faqAnswer">
                  <b>Second</b>, Pasadena has sought to increase commercial and residential development in
                  one of the most heavily diesel-polluted areas of the city and the state, East Pasadena.
                  State pollution data show that East Pasadena’s 22,000 residents already bear diesel-
                  pollution health risks that are worse than what 70 percent of all California residents
                  bear.
                </p>
              </li>
              <li>
                <h3 className="faqQuestion">HOW RISKY IS IT TO LIVE BESIDE THE FREEWAY?</h3>
                <p className="faqAnswer">
                  Living within 1000 feet of a freeway causes much higher rates of <b>asthma, heart
                  attacks, strokes, lung and other cancers, pre-term births, childhood obesity,
                  autism, and dementia</b>. Because of living for decades near the freeway, 1 in every 526,
                  625, and 769 adults living, respectively, near the I-210 near Lake Avenue, Altadena
                  Drive, and Rosemead Avenue, will <b>die prematurely</b> because of freeway-diesel
                  pollution. <b>Children will be hurt even more</b>. Roughly 1 in every 53, 63, and 77 children
                  living for decades, respectively, near the I-210 near Lake Avenue, Altadena Drive, and
                  Rosemead Avenue, will die prematurely because of freeway-diesel pollution.
                </p>
              </li>
              <li>
                <h3 className="faqQuestion">WON’T MERV-13 AIR FILTERS PROTECT BESIDE-FREEWAY RESIDENTS?</h3>
                <p className="faqAnswer">
                  No. The South Coast Air Management District says that MERV-13 filters will not bring
                  freeway diesel particles to a safe level. MERV-13 filters provide <b>no protection
                  whatsoever</b> against 95% of diesel particles, because virtually all diesel particles are
                  smaller than 0.1 µg, a size unable to be captured by MERV-13 filters. The city of
                  Pasadena grossly overestimates protections from MERV-13 air filters. That is why
                  California Air Quality officials advise against building any near-freeway homes.
                </p>
              </li>
              <li>
                <h3 className="faqQuestion">IS IT SAFE TO BE BESIDE THE FREEWAY FOR ONLY A DAY?</h3>
                <p className="faqAnswer">
                  No. There is no safe dose of freeway diesel particles, and no safe time period for
                  exposure to them because they are genotoxic carcinogens that have no safe dose. Only
                  one day’s exposure&mdash;to less than one-fourth of the government-allowed level of diesel
                  particles&mdash;causes a 1.4 increase in all-cause mortality&mdash;mainly from strokes, heart
                  attacks, COPD, and asthma. California studies show that even exposure to 1/200<sup>th</sup> of
                  daily allowed particles would kill 1 person among every 5,000 exposed people.
                </p>
              </li>
              <li>
                <h3 className="faqQuestion">IS IT SAFE FOR CHILDREN TO LIVE NEAR FREEWAYS?</h3>
                <p className="faqAnswer">
                  No. Children are roughly <b>10 times more sensitive than adults</b> when both receive the
                  same exposure to toxins, carcinogens, or air pollutants. Children are so sensitive
                  because they are still developing, their detoxification mechanisms are not yet in place,
                  and they take in proportionately far more air, water, food, and pollutants than do adults,
                  given their body weight.
                </p>
                <p className="faqAnswer">
                  When adults are exposed to freeway-diesel pollutants, they may contract various
                  diseases. However, when children are exposed to freeway-diesel pollutants, they not
                  only cause various diseases, but they damage their very ability to develop in a normal
                  way. <b>Diesel particles can cause children to have permanent, functional
                  impairment of their lungs and other organs, like the brain.</b> Children have “only once
                  chance” to develop a brain or lungs or heart or other organs. If diesel pollution interferes
                  with their normal organ development, there is no way to reverse the damage that this
                  pollution causes.
                </p>
              </li>
            </ol>
          </div>
        </div>
      </div>
      <div className="subsectionWrapper grey">
        <div className="subsection factSheet" id="fact-sheet">
          <h2 className="subsectionTitle">Fact Sheet and References</h2>
          <div className="textBlock">
            <p>
              <b>Diesel particles</b>, carcinogenic air toxins, cause 7 times more cancer than all the other
              187 US air toxins combined. They are <b>responsible for about 70% of the total cancer
              risk</b> in the South Coast Air Basin (California) from air pollution&mdash;an air-pollution cancer
              rate higher than anywhere in the US. The South Coast Air Basin violates both state and
              federal standards for particle pollution, including diesel particles.
            </p>
            <p>
              In the last several years, the city of Pasadena, in the South Coast Air Basin, has
              approved construction of roughly 1230 residences, mostly apartments, in the high-diesel-
              pollution zones beside, or within 700 feet of, Interstate-210. This is directly <b>contrary to
              decades of California Air Board recommendations</b> against any near-freeway
              residential development because of severe health harm from freeway-diesel-particle
              pollution.
            </p>
            <p>
              On 1-24-19, the city of Pasadena released its “<b>East Pasadena Draft Vision</b>.” (East
              Pasadena includes mainly the areas East of Hill Street and North of Colorado
              Boulevard.) The city’s “draft vision” called for massive rezoning of East Pasadena
              residential neighborhoods, so as to make them a “commercial destination center in the
              city.” However, <b>East Pasadena already has more freeway-caused diesel pollution
              than 70% of all California communities</b>. As a result, its 22,000 residents already suffer
              disproportionate health threats, compared to the rest of the city and the state.
            </p>
            <p>
              Living within 1000 feet of a freeway causes much “<b>higher rates of asthma, heart
              attacks, strokes, lung and other cancers, pre-term births, childhood obesity,
              autism, and dementia</b>.” The major culprit in all this harm is diesel particles. They are
              responsible for 70 percent of all Los Angeles County cancers caused by air pollution.
              Because of diesel pollution, the closer that people live to a freeway, the higher their
              cancer rates.
            </p>
            <p>
              <b>One in every 526</b>, 625, and 769 <b>adults</b>, respectively, living for several decades near
              the I-210 near Lake Avenue, Altadena Drive, and Rosemead Avenue, will die
              prematurely because of freeway-diesel pollution. This is a very high risk, hundreds of
              times higher than allowed.
            </p>
            <p>
              Roughly <b>1 in every 53</b>, 63, and 77 <b>children</b>, respectively, living for several decades
              near the I-210 near Lake Avenue, Altadena Drive, and Rosemead Avenue, will die
              prematurely because of freeway-diesel pollution. This is a very high risk for children,
              thousands of times higher than allowed.
            </p>
            <p>
              People residing even <b>1000 feet away</b> from these three locations (above) on the I-210
              freeway in Pasadena face roughly a <b>5-times higher cancer risk</b> from diesel
              particles than other California residents face.
            </p>
            <p>
              MERV-13 filters provide no protection whatsoever against 95% of diesel particles,
              because virtually all diesel particles are smaller than 0.1 µg. These <b>nano-sized diesel
              particles easily get through MERV-13 filters</b>. California air-quality officials say that
              because air filters do not significantly remove freeway-diesel-particle risks, ho homes
              should be built close to freeways.
            </p>
            <p>
              There is <b>no safe dose of, and no short and therefore safe exposure to, diesel
              particles</b>. Even the tiniest exposure for only one day causes a statistically significant
              increase in daily mortality.
            </p>
            <p>
              <b>Children are roughly 10 times more sensitive than adults</b> when both receive the
              same exposure to toxins, carcinogens, or air pollutants like diesel particles.
            </p>
            <p>
              <b>Children have only once chance to develop a brain</b> or lungs or heart or other
              organs. If diesel pollution interferes with their organ development, there is no way to
              reverse the damage that pollution causes.
            </p>
            <p>
              California health officials say that <b>cities should re-zone heavy-traffic and near-
              freeway areas to exclude new homes</b>. They point out that cities could strengthen
              building standards, just as they have for earthquake protection, and force developers to
              design buildings in ways that <b>reduce or eliminate residents’ exposure to polluted
              air</b>. Such designs include requiring windows that do not open and requiring powerful,
              whole-building, commercial-grade, air-filtration systems that give residents clean air.
            </p>
          </div>
        </div>
      </div>
  </section>
)

export default ProposalComponent
